import { accountEntityName } from '../../../../backend/features/accounts/shared';
import { mapAccountFilterReverse } from '../../../../ui/features/groups/utils/mapAccountFilterReverse';
import { useContactManagementTranslation } from '../../../../ui/hooks/useContactManagementTranslation';
import { ContactManagementConfigContext } from '../../../../ui/providers';
import { maxUrlSearchLength } from '../../../../utils';
import { useDataTableUsingState } from '@juvare/common-data-table-ui/DataTable';
import { FiltersBar } from '@juvare/common-data-table-ui/FiltersBar';
import { useDynamicColumnDefinitions } from '@juvare/platform-sdk-react/ui/components/EntityTable/dynamicColumns';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAccountColumns } from './useAccountColumns';
import { useAccountFilter } from './useAccountFilter';
import { useAccountList } from './useAccountList';
import { useAccountFilterConfig } from './useAccountsFilterConfig';
import { useDefaultAdvancedSearchItems } from './useDefaultAdvancedSearchItems';
export function useAccountsTable({ location, useDataTableFunction = useDataTableUsingState, onRowClick, rowSelectionEnabled, requireOrgRoleInStatusFilter, initialAccountFilterData, initialStorageClientFilter, staticStorageClientFilter, showAccountStatusFilter, showAccountSystemRoleFilter, showOrganizationRoleFilter, showOrganizationStatusFilter, showAllAccounts, saveFilterLabel, onSaveFilter, initialSelectedRowIds, showAccountsIManage, defaultRowsPerPage, }) {
    var _a, _b;
    const { t } = useContactManagementTranslation();
    const { accountsTableCustomization } = React.useContext(ContactManagementConfigContext);
    const defaultAdvancedSearchItems = useDefaultAdvancedSearchItems();
    const advancedSearchItems = (accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.onGetAdvancedSearchItems)
        ? accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.onGetAdvancedSearchItems(defaultAdvancedSearchItems)
        : defaultAdvancedSearchItems;
    const settings = useSelector(getSettings);
    const { data: dynamicColumnData } = useDynamicColumnDefinitions(accountEntityName, settings.dynamicColumnsEnabled); // TODO handle dynamic filter loading and error
    const useResolvedAccountFilter = (_a = accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.overrideUseAccountFilter) !== null && _a !== void 0 ? _a : useAccountFilter;
    const resolvedMapAccountFilterReverse = (_b = accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.overrideMapAccountFilterReverse) !== null && _b !== void 0 ? _b : mapAccountFilterReverse;
    const params = {
        useDataProvider: (request) => useAccountList(request, useResolvedAccountFilter, advancedSearchItems, location, dynamicColumnData, staticStorageClientFilter, showAllAccounts, showAccountsIManage),
        onRowClick,
        rowSelectionEnabled,
        defaultSortColumnKey: 'displayName',
        maxUrlSearchLength,
        defaultRowsPerPage,
        initialFilterData: initialAccountFilterData !== null && initialAccountFilterData !== void 0 ? initialAccountFilterData : (initialStorageClientFilter ? resolvedMapAccountFilterReverse(initialStorageClientFilter, dynamicColumnData) : undefined),
        initialSelectedRowIds,
    };
    const { dataTableProps, filtersBarProps, appliedFilterData, clearActiveRow, reload, setSelectedRowIds, setActiveRowId } = useDataTableFunction(params);
    const staticOrganizationId = (location === null || location === void 0 ? void 0 : location.name) === 'org-members' ? location.organizationId : undefined;
    const columns = useAccountColumns({
        showAccountStatus: !!showAccountStatusFilter,
        staticOrganizationId: staticOrganizationId,
    });
    const filterConfig = useAccountFilterConfig({
        requireOrgRoleInStatusFilter,
        showAccountStatusFilter,
        showAccountSystemRoleFilter,
        staticOrganizationId,
        showOrganizationRoleFilter,
        showOrganizationStatusFilter,
        extendFilters: accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.extendFilters,
        advancedSearchItems,
    });
    const defaultDataTableProps = Object.assign(Object.assign({}, dataTableProps), { title: t('panels.accounts.title'), columns: columns, filtersBar: React.createElement(FiltersBar, Object.assign({}, filtersBarProps, { config: filterConfig, onSaveFilter: onSaveFilter, saveFilterLabel: saveFilterLabel })), tableSx: { tableLayout: 'fixed' } });
    return {
        dataTableProps: (accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.getDataTableProps)
            ? accountsTableCustomization.getDataTableProps(defaultDataTableProps, appliedFilterData, location)
            : defaultDataTableProps,
        filtersBarProps: filtersBarProps,
        appliedFilterData: appliedFilterData,
        clearActiveRow: clearActiveRow,
        setActiveRowId: setActiveRowId,
        setSelectedRowIds: setSelectedRowIds,
        reload: reload,
        dynamicColumnData,
    };
}
